import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  BuildingStorefrontIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  CalendarIcon,
  KeyIcon,
  CreditCardIcon,
} from "@heroicons/react/24/outline";
import api from "../../api";

function JewellerProfile() {
  const { id } = useParams();
  const [jeweller, setJeweller] = useState(null);

  useEffect(() => {
    api
      .get(`/jewellers/${id}`)
      .then((res) => setJeweller(res.data))
      .catch((err) => {
        console.error(err.response || err.message);
        alert("Failed to load jeweller profile");
      });
  }, [id]);

  if (!jeweller) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold flex items-center gap-2 text-purple-600">
          <BuildingStorefrontIcon className="h-8 w-8 text-purple-600" />
          {jeweller.business_name}
        </h1>
        <span
          className={`px-3 py-1 text-sm font-semibold rounded-full ${jeweller.status === "Active"
            ? "bg-green-100 text-green-700"
            : "bg-red-100 text-red-600"
            }`}
        >
          {jeweller.status}
        </span>
      </div>

      {/* Profile Card */}
      <div className="bg-white p-6 rounded-2xl shadow-lg space-y-6 border border-gray-100">
        {/* Logo */}
        {jeweller.logo && (
          <div className="flex justify-center">
            <img
              src={`http://localhost:8000/storage/${jeweller.logo}`}
              alt="Logo"
              className="w-32 h-32 object-contain rounded-xl border border-gray-200 shadow-sm"
            />
          </div>
        )}

        {/* Details */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Detail icon={<UserIcon className="h-5 w-5 text-purple-500" />} label="Owner" value={jeweller.user?.name} />
          <Detail icon={<EnvelopeIcon className="h-5 w-5 text-purple-500" />} label="Email" value={jeweller.user?.email} />
          <Detail icon={<PhoneIcon className="h-5 w-5 text-purple-500" />} label="Phone" value={jeweller.user?.phone} />
          <Detail icon={<KeyIcon className="h-5 w-5 text-purple-500" />} label="Password" value={jeweller.user?.visible_password || "—"} />
          <Detail icon={<MapPinIcon className="h-5 w-5 text-purple-500" />} label="Address" value={jeweller.address} />
          <Detail icon={<CalendarIcon className="h-5 w-5 text-purple-500" />} label="Registered On" value={new Date(jeweller.created_at).toLocaleString()} />
          <Detail icon={<MapPinIcon className="h-5 w-5 text-purple-500" />} label="Latitude" value={jeweller.latitude} />
          <Detail icon={<MapPinIcon className="h-5 w-5 text-purple-500" />} label="Longitude" value={jeweller.longitude} />
        </div>

        {/* Subscription Section */}
        <div className="mt-8">
          <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
            <CreditCardIcon className="h-6 w-6 text-purple-600" />
            Subscription Plan
          </h2>

          {jeweller.subscription ? (
            <div className="mt-3 bg-indigo-50 p-5 rounded-xl border border-indigo-100">
              <p className="text-gray-700">
                <strong>ID:</strong> {jeweller.subscription.id}
              </p>
              <p className="text-gray-700">
                <strong>Status:</strong>{" "}
                <span className={`font-semibold ${jeweller.subscription.status === "active" ? "text-green-600" : "text-red-600" }`}>
                  {jeweller.subscription.status}
                </span>
              </p>
              <p className="text-gray-700">
                <strong>Start Date:</strong>{" "}
                {new Date(jeweller.subscription.start_date).toLocaleDateString()}
              </p>
              <p className="text-gray-700">
                <strong>Expiry Date:</strong>{" "}
                {new Date(jeweller.subscription.expiry_date).toLocaleDateString()}
              </p>
            </div>
          ) : (
            <p className="text-gray-600 mt-3">No active subscription</p>
          )}
        </div>
      </div>
    </div>
  );
}

function Detail({ icon, label, value }) {
  return (
    <div className="flex items-start gap-3 bg-gray-50 p-3 rounded-lg hover:bg-indigo-50 transition">
      <div className="p-2 bg-indigo-100 rounded-full">{icon}</div>
      <div>
        <p className="text-sm text-gray-500">{label}</p>
        <p className="font-semibold text-gray-800">{value || "—"}</p>
      </div>
    </div>
  );
}

export default JewellerProfile;
